/**
 * War Board Snapshot
 * Compact live stats card read from localStorage ('salesWarBoard') to mirror
 * the MassRVGuy May Sales War Board. Rendered inside the Sales Tracker tab.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Target, Flame, Clock } from 'lucide-react';
import { getAuthToken } from '../../utils/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WAR_BOARD_GOAL = 15;
// Auto-track the current month so the snapshot rolls over each new month
const _now = new Date();
const WAR_BOARD_TARGET_MONTH = _now.getMonth();
const WAR_BOARD_TARGET_YEAR = _now.getFullYear();
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const TARGET_MONTH_NAME = MONTH_NAMES[WAR_BOARD_TARGET_MONTH];

const WarBoardSnapshot = ({ isDark }) => {
  const [warBoard, setWarBoard] = useState(null);
  const [daysLeftInMonth, setDaysLeftInMonth] = useState(0);

  useEffect(() => {
    const loadWarBoard = () => {
      try {
        const raw = localStorage.getItem('salesWarBoard');
        if (raw) setWarBoard(JSON.parse(raw));
      } catch (e) { /* ignore */ }

      const now = new Date();
      const endOfMonth = new Date(WAR_BOARD_TARGET_YEAR, WAR_BOARD_TARGET_MONTH + 1, 0, 23, 59, 59);
      setDaysLeftInMonth(Math.max(0, Math.ceil((endOfMonth - now) / (1000 * 60 * 60 * 24))));
    };

    // Fetch fresh units from backend so the snapshot is accurate even if the
    // user hasn't opened the full War Board this session.
    const syncFromBackend = async () => {
      try {
        const token = getAuthToken();
        if (!token) return;
        const res = await axios.get(`${API}/dealer/sales/analytics/summary`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const targetKey = `${WAR_BOARD_TARGET_YEAR}-${String(WAR_BOARD_TARGET_MONTH + 1).padStart(2, '0')}`;
        const monthData = (res.data?.monthly_data || []).find((d) => d.month === targetKey);
        const units = monthData && typeof monthData.units === 'number' ? monthData.units : 0;
        const raw = localStorage.getItem('salesWarBoard');
        const wb = raw ? JSON.parse(raw) : { currentUnits: 0, deals: [], dailyProgress: {}, lastResetDate: '' };
        if (wb.currentUnits !== units) {
          wb.currentUnits = units;
          localStorage.setItem('salesWarBoard', JSON.stringify(wb));
          setWarBoard(wb);
        }
      } catch (e) { /* silent */ }
    };

    loadWarBoard();
    syncFromBackend();
    window.addEventListener('storage', loadWarBoard);
    const interval = setInterval(loadWarBoard, 60000);
    return () => {
      window.removeEventListener('storage', loadWarBoard);
      clearInterval(interval);
    };
  }, []);

  if (!warBoard) return null;

  const units = warBoard.currentUnits ?? 0;
  const remaining = Math.max(0, WAR_BOARD_GOAL - units);
  const pace = daysLeftInMonth > 0 ? (remaining / daysLeftInMonth).toFixed(1) : '0';
  const progressPct = Math.min(100, (units / WAR_BOARD_GOAL) * 100);
  const pendingDeals = (warBoard.deals || []).filter((d) => !d.sold).length;

  return (
    <div
      className={`rounded-xl p-4 mb-6 border ${isDark ? 'bg-gradient-to-r from-[#004E86]/30 to-[#F2A900]/10 border-[#004E86]/50' : 'bg-gradient-to-r from-[#004E86]/5 to-[#F2A900]/10 border-[#004E86]/20'}`}
      data-testid="war-board-snapshot"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Target size={18} className="text-[#F2A900]" />
          <h3 className={`font-bold text-sm uppercase tracking-wide ${isDark ? 'text-white' : 'text-[#004E86]'}`}>
            {TARGET_MONTH_NAME} Sales War Board
          </h3>
          <Flame size={14} className="text-orange-500" />
        </div>
        <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          Live · auto-synced
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
        <div data-testid="war-units">
          <p className={`text-xs uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Units Sold</p>
          <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-[#004E86]'}`}>
            {units}
            <span className="text-sm font-normal opacity-60">/{WAR_BOARD_GOAL}</span>
          </p>
        </div>
        <div data-testid="war-remaining">
          <p className={`text-xs uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Remaining</p>
          <p className={`text-2xl font-bold ${remaining === 0 ? 'text-green-500' : 'text-[#F2A900]'}`}>{remaining}</p>
        </div>
        <div data-testid="war-days-left">
          <p className={`text-xs uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            <Clock size={10} className="inline mr-1" /> Days Left
          </p>
          <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{daysLeftInMonth}</p>
        </div>
        <div data-testid="war-pace">
          <p className={`text-xs uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Pace Needed</p>
          <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {pace}
            <span className="text-sm font-normal opacity-60">/day</span>
          </p>
        </div>
        <div data-testid="war-pending">
          <p className={`text-xs uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Pending Deals</p>
          <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{pendingDeals}</p>
        </div>
      </div>
      <div className={`w-full h-2 rounded-full overflow-hidden ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
        <div
          className="h-full bg-gradient-to-r from-[#004E86] to-[#F2A900] transition-all duration-500"
          style={{ width: `${progressPct}%` }}
          data-testid="war-progress-bar"
        />
      </div>
      <p className={`text-xs mt-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
        {progressPct.toFixed(0)}% of goal · Open the <span className="font-semibold">War Board</span> in Resources → Sales Accountability to update.
      </p>
    </div>
  );
};

export default WarBoardSnapshot;
